import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiHeart, FiUsers, FiTrendingUp, FiShield, FiGlobe } = FiIcons;

function About() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const values = [
    {
      icon: FiShield,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards in all our dealings, ensuring transparency and trust in every client relationship.'
    },
    {
      icon: FiTarget,
      title: 'Excellence',
      description: 'We strive for outstanding results in every case, combining expertise with dedication to achieve the best outcomes for our clients.'
    },
    {
      icon: FiUsers,
      title: 'Accessibility',
      description: 'We make quality legal services available to diverse communities, bridging gaps and ensuring equal access to justice.'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation',
      description: 'We embrace modern legal solutions and technology to provide efficient, effective services that meet contemporary needs.'
    },
    {
      icon: FiHeart,
      title: 'Ubuntu',
      description: 'We believe in the interconnectedness of our community and our responsibility to contribute to South Africa\'s development.'
    },
    {
      icon: FiGlobe,
      title: 'Community',
      description: 'We are committed to contributing to the development of South Africa\'s legal landscape and economic growth.'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
              About <span className="text-gold-600">Our Firm</span>
            </h1>
            <p className="font-opensans text-xl text-gray-200 max-w-3xl mx-auto">
              Learn about our 25-year history of legal excellence in Johannesburg and meet our expert attorneys specializing in corporate and employment law.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Firm History Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-6">
                Our History
              </h2>
              <p className="font-opensans text-gray-700 mb-6 leading-relaxed">
                Given Ndlovu & Thibela Attorneys Legal Practitioners was founded in 1998 by Senior Partner Advocate Given Ndlovu, who envisioned a law firm that would bridge the gap between corporate excellence and community accessibility. What began as a small practice in Sandton has grown into one of Johannesburg's most trusted legal institutions.
              </p>
              <p className="font-opensans text-gray-700 mb-6 leading-relaxed">
                Our firm was built on the principle that quality legal representation should be both professionally rigorous and personally compassionate. We understand that behind every legal matter is a person or business with real concerns, aspirations, and challenges.
              </p>
              <p className="font-opensans text-gray-700 leading-relaxed">
                Over the past 25 years, we have handled some of Gauteng's most significant legal matters, from complex corporate restructuring to landmark employment law cases. Our commitment to excellence and community service has made us a cornerstone of Johannesburg's legal landscape.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://media.phst.co.za/file/gnt-about-given3.jpg"
                alt="Given Ndlovu"
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-6">
              Our Mission
            </h2>
            <p className="font-opensans text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              To provide exceptional legal services that protect our clients' interests while contributing to the development of South Africa's legal landscape and economic growth. We are committed to delivering results that matter, with integrity and excellence as our guiding principles.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-4">
              Our Values
            </h2>
            <p className="font-opensans text-xl text-gray-600 max-w-3xl mx-auto">
              These core values guide every decision we make and every service we provide, ensuring that we remain true to our commitment to excellence and community.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-800 text-gold-600 rounded-full mb-6">
                  <SafeIcon icon={value.icon} className="w-8 h-8" />
                </div>
                <h3 className="font-playfair font-bold text-2xl text-navy-800 mb-4">
                  {value.title}
                </h3>
                <p className="font-opensans text-gray-700 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-4">
              Our Journey
            </h2>
            <p className="font-opensans text-xl text-gray-600">
              Key milestones in our 25-year history
            </p>
          </div>
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center space-x-8"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-navy-800 text-gold-600 rounded-full flex items-center justify-center">
                <span className="font-playfair font-bold text-lg">1998</span>
              </div>
              <div>
                <h3 className="font-playfair font-bold text-2xl text-navy-800 mb-2">
                  Firm Founded
                </h3>
                <p className="font-opensans text-gray-700">
                  Advocate Given Ndlovu establishes the practice in Sandton with a vision of bridging corporate excellence and community accessibility.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center space-x-8"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-navy-800 text-gold-600 rounded-full flex items-center justify-center">
                <span className="font-playfair font-bold text-lg">2005</span>
              </div>
              <div>
                <h3 className="font-playfair font-bold text-2xl text-navy-800 mb-2">
                  Major Expansion
                </h3>
                <p className="font-opensans text-gray-700">
                  Addition of employment law and commercial litigation divisions, establishing ourselves as a full-service firm.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-center space-x-8"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-navy-800 text-gold-600 rounded-full flex items-center justify-center">
                <span className="font-playfair font-bold text-lg">2015</span>
              </div>
              <div>
                <h3 className="font-playfair font-bold text-2xl text-navy-800 mb-2">
                  Recognition & Growth
                </h3>
                <p className="font-opensans text-gray-700">
                  Recognized as one of Johannesburg's leading law firms, with significant growth in our corporate and employment law practices.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center space-x-8"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-navy-800 text-gold-600 rounded-full flex items-center justify-center">
                <span className="font-playfair font-bold text-lg">2024</span>
              </div>
              <div>
                <h3 className="font-playfair font-bold text-2xl text-navy-800 mb-2">
                  Digital Innovation
                </h3>
                <p className="font-opensans text-gray-700">
                  Launch of our digital platform and enhanced client services, continuing our commitment to innovation and accessibility.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;